import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, Search, UserX, Download, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function AdminNewsletter() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/newsletter/subscribers");
      setSubs(data || []);
    } catch { toast.error("Failed to load subscribers"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const unsubscribe = async (email) => {
    if (!window.confirm(`Unsubscribe ${email}?`)) return;
    try {
      await api.delete(`/admin/newsletter/subscribers/${encodeURIComponent(email)}`);
      toast.success("Unsubscribed");
      load();
    } catch { toast.error("Failed"); }
  };

  const filtered = subs.filter((s) => {
    if (!q.trim()) return true;
    const t = q.toLowerCase();
    return (s.email || "").toLowerCase().includes(t) || (s.full_name || "").toLowerCase().includes(t);
  });

  const downloadCsv = () => {
    const rows = [["Email", "Name", "Phone", "Goal", "Source", "Step", "Subscribed", "Status"]];
    filtered.forEach((s) => rows.push([
      s.email, s.full_name || "", s.phone || "", (s.goal || "").replace(/\n/g, " "),
      s.source || "", s.current_step ?? -1,
      s.subscribed_at || "", s.unsubscribed ? "Unsubscribed" : "Active",
    ]));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `newsletter-subscribers-${Date.now()}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const activeCount = subs.filter((s) => !s.unsubscribed).length;

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="admin-newsletter-page">
      <header className="flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold">Newsletter</p>
          <h1 className="font-display text-3xl text-white uppercase tracking-tight">Drip Subscribers</h1>
          <p className="text-sm text-zinc-400 mt-1">14-day nurture sequence audience. {activeCount} active · {subs.length - activeCount} unsubscribed.</p>
        </div>
        <Button onClick={downloadCsv} variant="outline" className="border-[#27272A] bg-[#0F0F0F] text-zinc-300 hover:text-white uppercase tracking-widest font-bold text-xs">
          <Download className="h-4 w-4 mr-2" /> Export CSV
        </Button>
      </header>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email or name…" className="pl-9 bg-[#050505] border-[#27272A] text-white rounded-sm" data-testid="newsletter-search" />
        </div>
        <div className="text-xs text-zinc-500">{filtered.length} shown</div>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-[#27272A] p-12 text-center text-zinc-500"><Mail className="h-8 w-8 mx-auto mb-2 text-zinc-700" /> No subscribers yet.</div>
      ) : (
        <div className="border border-[#27272A] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0F0F0F] border-b border-[#27272A] text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="p-3 text-left">Email</th>
                <th className="p-3 text-left">Name</th>
                <th className="p-3 text-left">Source</th>
                <th className="p-3 text-left">Step</th>
                <th className="p-3 text-left">Subscribed</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.email} className="border-b border-[#27272A] hover:bg-[#0F0F0F]" data-testid={`newsletter-row-${s.email}`}>
                  <td className="p-3 text-zinc-200">{s.email}</td>
                  <td className="p-3 text-zinc-400">{s.full_name || "—"}</td>
                  <td className="p-3 text-zinc-500 text-xs">{s.source || "—"}</td>
                  <td className="p-3 text-zinc-500 text-xs">{(s.current_step ?? -1) < 0 ? "queued" : `${(s.current_step ?? 0) + 1} / 7`}</td>
                  <td className="p-3 text-zinc-600 text-[11px]">{s.subscribed_at ? new Date(s.subscribed_at).toLocaleDateString() : "—"}</td>
                  <td className="p-3">
                    {s.unsubscribed ? (
                      <span className="inline-flex items-center gap-1 text-zinc-500 text-[10px] uppercase tracking-widest font-bold"><XCircle className="h-3 w-3" />Unsubscribed</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-emerald-400 text-[10px] uppercase tracking-widest font-bold"><CheckCircle2 className="h-3 w-3" />Active</span>
                    )}
                  </td>
                  <td className="p-3 text-right">
                    {!s.unsubscribed && (
                      <Button variant="ghost" size="sm" onClick={() => unsubscribe(s.email)} className="text-zinc-400 hover:text-red-400 h-8 px-2" data-testid={`newsletter-unsub-${s.email}`}>
                        <UserX className="h-3 w-3" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
